import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Brain, FileWarning, Loader2, Play } from 'lucide-react';
import { Card } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  getLearningOverview,
  listLearningProposals,
  runLearningCycleNow,
  type LearningOverview,
} from '@/lib/api-client';

// Legal Brain B7: the learning agent's control panel — latest run report,
// blocked acquisitions awaiting manual sourcing, open gaps, corpus growth,
// drafted prompt proposals (B6), and the manual run button. Dark stone +
// amber, matching the admin pages.

function fmtDateTime(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export function AdminLearning() {
  const [overview, setOverview] = useState<LearningOverview | null>(null);
  const [proposals, setProposals] = useState<Array<{ name: string; content: string }>>([]);
  const [openProposal, setOpenProposal] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getLearningOverview();
      setOverview(data);
      // While a cycle runs, poll so the report appears as soon as it finishes.
      if (data.cycleRunning && pollRef.current === null) {
        pollRef.current = setInterval(() => void refresh(), 5000);
      }
      if (!data.cycleRunning && pollRef.current !== null) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the learning overview.');
    }
  }, []);

  useEffect(() => {
    void refresh();
    void listLearningProposals().then(setProposals).catch(() => setProposals([]));
    return () => {
      if (pollRef.current !== null) {
        clearInterval(pollRef.current);
      }
    };
  }, [refresh]);

  async function handleRunNow(): Promise<void> {
    setError(null);
    setStarting(true);
    try {
      await runLearningCycleNow();
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not start the cycle.');
    } finally {
      setStarting(false);
    }
  }

  const run = overview?.latestRun ?? null;

  return (
    <div className="min-h-screen bg-stone-950 px-5 py-6 sm:px-6">
      <div className="mx-auto w-full max-w-4xl space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl italic text-stone-100">Learning</h1>
          <Link
            to="/hearing"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-amber-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Hearing
          </Link>
        </div>

        {error ? <p className="text-sm text-red-400">{error}</p> : null}

        <Card variant="default" padding="md" className="space-y-3 border-amber-900/30 bg-amber-950/10">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Brain className="h-4 w-4 text-amber-500" aria-hidden />
              <p className="text-sm text-stone-300">
                Corpus: <span className="text-stone-100">{overview?.corpus.authorities ?? '—'}</span>{' '}
                authorities · {overview?.corpus.chunks ?? '—'} chunks
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
                Nightly cron:{' '}
                {overview ? (
                  overview.cronEnabled ? (
                    <span className="text-emerald-400">
                      on · {String(overview.cronHourMelbourne).padStart(2, '0')}:00 Melbourne
                    </span>
                  ) : (
                    <span className="text-stone-400">disabled</span>
                  )
                ) : (
                  '—'
                )}
              </span>
              <button
                type="button"
                onClick={() => void handleRunNow()}
                disabled={starting || overview?.cycleRunning === true}
                className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-stone-950 transition-colors hover:bg-amber-700 disabled:opacity-40"
              >
                {overview?.cycleRunning ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <Play className="h-4 w-4" aria-hidden />
                )}
                {overview?.cycleRunning ? 'Cycle running…' : 'Run learning cycle now'}
              </button>
            </div>
          </div>
          {overview && overview.corpus.growth.length > 1 ? (
            <p className="text-[11px] text-stone-500">
              Growth (12 weeks):{' '}
              {overview.corpus.growth
                .map((g) => `${new Date(g.week).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}: +${g.authorities}`)
                .join(' · ')}
            </p>
          ) : null}
          <p className="text-[11px] text-stone-500">
            Queue:{' '}
            {overview
              ? Object.entries(overview.queue)
                  .map(([k, v]) => `${v} ${k.toLowerCase()}`)
                  .join(' · ') || 'empty'
              : '—'}
            {overview && overview.failedCount > 0 ? ` · ${overview.failedCount} failed` : ''}
          </p>
        </Card>

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Latest run {run ? `(${run.trigger.toLowerCase()}, ${fmtDateTime(run.startedAt)})` : ''}
          </p>
          {run ? (
            <Card variant="default" padding="md" className="space-y-2">
              <p className="text-[11px] text-stone-400">
                {run.gapsReviewed} gaps reviewed · {run.itemsFetched} fetched · {run.itemsIngested}{' '}
                ingested · {run.itemsFailed} failed · self-test{' '}
                <span className={run.selfTestFailed > 0 ? 'text-red-400' : 'text-emerald-400'}>
                  {run.selfTestPassed} passed / {run.selfTestFailed} failed
                </span>
                {run.finishedAt === null ? ' · still running' : ''}
              </p>
              <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border border-stone-800/60 bg-stone-900/60 p-3 text-[11px] leading-relaxed text-stone-300">
                {run.reportText || '(report pending)'}
              </pre>
            </Card>
          ) : (
            <p className="text-sm text-stone-500">No learning run yet.</p>
          )}
        </section>

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Blocked — needs manual sourcing via the authorities inbox (
            {overview?.blocked.length ?? 0})
          </p>
          {!overview || overview.blocked.length === 0 ? (
            <p className="text-sm text-stone-500">Nothing blocked.</p>
          ) : (
            <div className="space-y-2">
              {overview.blocked.map((b) => (
                <div
                  key={b.id}
                  className="flex items-start gap-2 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-2"
                >
                  <FileWarning className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden />
                  <div className="min-w-0">
                    <p className="text-sm text-stone-200">
                      {b.citation}
                      {b.titleGuess ? <span className="text-stone-500"> — {b.titleGuess}</span> : null}
                    </p>
                    <p className="text-[11px] text-stone-500">{b.lastError ?? 'blocked'}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Open knowledge gaps ({overview?.gaps.length ?? 0})
          </p>
          {!overview || overview.gaps.length === 0 ? (
            <p className="text-sm text-stone-500">No open gaps — nothing has needed verification.</p>
          ) : (
            <div className="space-y-1.5">
              {overview.gaps.map((g) => (
                <div
                  key={g.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-stone-800/60 bg-stone-900/60 px-3 py-1.5"
                >
                  <p className="min-w-0 truncate text-sm text-stone-300">{g.topic}</p>
                  <p className="shrink-0 text-[10px] uppercase tracking-[0.15em] text-stone-500">
                    {g.status.toLowerCase()} · ×{g.repeatCount}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="space-y-2">
          <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500">
            Prompt improvement proposals — drafts only, never auto-applied ({proposals.length})
          </p>
          {proposals.length === 0 ? (
            <p className="text-sm text-stone-500">No drafts. The weekly analysis writes one when DOWN feedback accumulates.</p>
          ) : (
            <div className="space-y-2">
              {proposals.map((p) => (
                <div key={p.name} className="rounded-md border border-stone-800/60 bg-stone-900/60">
                  <button
                    type="button"
                    onClick={() => setOpenProposal(openProposal === p.name ? null : p.name)}
                    className="w-full px-3 py-2 text-left text-sm text-stone-200 hover:text-amber-300"
                  >
                    {p.name}
                  </button>
                  {openProposal === p.name ? (
                    <pre className="max-h-96 overflow-auto whitespace-pre-wrap border-t border-stone-800/60 p-3 text-[11px] leading-relaxed text-stone-300">
                      {p.content}
                    </pre>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
